import { type Player, PlayerStatus } from '@infiltre/shared';
import { motion } from 'motion/react';
import { useT } from '@/shared/lib/i18n';
import { useResolvedLayout } from '@/shared/lib/layout';
import { mugshotForPlayer } from '@/shared/lib/mugshot';
import { MugshotPortrait, PaperSheet, PhaseHeader } from '@/shared/ui';
import { ReactionBar } from './ReactionBar';
import { OrderArrow } from './speaking/OrderArrow';
import { ReadyToVoteCta } from './speaking/ReadyToVoteCta';

export type SpeakingOrderMode = 'standalone' | 'online';

export type SpeakingOrderProps = {
  players: readonly Player[];
  onReadyToVote: () => void;
  /** Standalone hides reaction bar + adds order numbers + direction arrow. */
  mode?: SpeakingOrderMode;
  /**
   * Hide the "Ready to vote" CTA (and substitute a "waiting for host" line)
   * for non-host online players. They still see the speaking ring + reaction
   * bar — only the host owns the OpenVote trigger. Default: true.
   */
  canReadyToVote?: boolean;
};

const TILTS = [-4, 3, -2, 4, -3, 5, -5, 2];

export const SpeakingOrder = ({
  players,
  onReadyToVote,
  mode = 'online',
  canReadyToVote = true,
}: SpeakingOrderProps) => {
  const t = useT();
  const layout = useResolvedLayout();
  const isDesktop = layout === 'desktop';
  const alive = players.filter((p) => p.status === PlayerStatus.Alive);
  const isStandalone = mode === 'standalone';

  const ringSize = isDesktop ? 460 : 320;
  const ringR = isDesktop ? 175 : 120;
  const cardW = isDesktop ? 96 : 76;
  const cardH = isDesktop ? 116 : 92;
  const cx = ringSize / 2;
  const cy = ringSize / 2;

  return (
    <main className="mx-auto flex h-full w-full max-w-5xl flex-col">
      <PhaseHeader
        eyebrow={t('round.speaking.phase_eyebrow')}
        title={t('round.speaking.order_title')}
        descriptor={t('round.speaking.descriptor')}
      />

      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-6 pb-2">
        <div className="relative flex-shrink-0" style={{ width: ringSize, height: ringSize }}>
          {isStandalone && alive.length > 1 && (
            <OrderArrow ringSize={ringSize} ringR={ringR} count={alive.length} />
          )}

          {alive.map((p, i) => {
            const angle = (i / alive.length) * Math.PI * 2 - Math.PI / 2;
            const x = Math.cos(angle) * ringR + cx - cardW / 2;
            const y = Math.sin(angle) * ringR + cy - cardH / 2;
            const tilt = TILTS[i % TILTS.length]!;
            const fileTag = `F-${String(((i + 1) * 73) % 1000).padStart(3, '0')}`;
            const initial = p.name.charAt(0).toUpperCase();
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, scale: 0.8, rotate: 0 }}
                animate={{ opacity: 1, scale: 1, rotate: tilt }}
                transition={{ delay: 0.05 * i, duration: 0.32 }}
                className="absolute"
                style={{ left: x, top: y, width: cardW }}
              >
                <PaperSheet
                  tone="cream"
                  seed={p.id}
                  className="relative px-1 pt-1 pb-1.5"
                  style={{
                    boxShadow: 'var(--shadow-paper-soft)',
                  }}
                >
                  {isStandalone && (
                    <span
                      aria-hidden
                      className="-top-1.5 -left-1.5 absolute z-10 flex h-5 w-5 items-center justify-center rounded-full border-2 border-ink bg-cream font-display font-extrabold text-[10px] text-ink"
                      style={{ boxShadow: 'var(--shadow-stamp-xs)' }}
                    >
                      {i + 1}
                    </span>
                  )}
                  <div
                    className="relative flex w-full items-center justify-center overflow-hidden border border-ink/15"
                    style={{ height: cardH - 30 }}
                  >
                    <MugshotPortrait config={mugshotForPlayer(p)} />
                    <span className="absolute bottom-0.5 left-1 font-mono text-[7px] text-ink/55 tracking-[0.18em]">
                      {fileTag}
                    </span>
                    <span className="absolute right-1 bottom-0.5 font-mono text-[8px] text-ink/55">
                      {initial}
                    </span>
                  </div>
                </PaperSheet>
                <div className="mt-0.5 truncate text-center font-hand text-[14px] text-cream leading-tight">
                  {p.name}
                </div>
              </motion.div>
            );
          })}

          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 text-center">
            <div className="type text-[10px] text-cream/55 uppercase tracking-[0.22em]">
              {t('round.speaking.order_eyebrow')}
            </div>
            <div className="font-display font-extrabold text-[28px] text-cream italic uppercase leading-none sm:text-[36px]">
              {alive.length} {alive.length === 1 ? 'voix' : 'voix'}
            </div>
          </div>
        </div>

        {!isStandalone && (
          <div className="flex flex-col items-center gap-1.5">
            <div className="text-[12px] text-cream/55 italic">{t('round.speaking.hint')}</div>
            <ReactionBar />
          </div>
        )}
      </div>

      <div className="px-5 pt-2 pb-5">
        <ReadyToVoteCta canReadyToVote={canReadyToVote} onReadyToVote={onReadyToVote} />
      </div>
    </main>
  );
};
